import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  AgentUserNamesApiResponse,
  FeedbackCampaignSubmitRequest,
  GetUserFeedbackDataRequest,
  SaveFeedBackRequestAgentRequest,
  UpdateFeedbackAgentAssignmentRequest,
  UserFeedbackApiResponse
} from '../models/feedback.model';
import {
  FeedbackRequestItem,
  FetchFeedBackRequestsPayload,
  PendingFeedbackRequestsResponse,
  AssignedFeedbackRequestsResponse,
  GetFeedBackReviewDataRequest,
  GetFeedBackReviewDataResponse,
  DisplayAgentReviewRequest,
  DisplayAgentReviewResponse
} from '../models/feedback-request.model';
import {
  FeedAgentAcceptPayload,
  FeedAgentAcceptResponse,
  LatestFeedbackRequestApiResponse,
  FeedbackChatHistoryRequest,
  FeedbackChatHistoryResponse,
  MessageSendToFbAgentPayload,
  MessageSendToFbAgentResponse,
  FeedbackExitAgentPayload,
  FeedbackExitAgentResponse,
  StoreAgentFeedBackPayload,
  StoreAgentFeedBackResponse
} from '../models/feedback-notification.model';

@Injectable({
  providedIn: 'root'
})
export class FeedbackService {

  constructor(private http: HttpClient) {}

  getUserFeedbackData(fbType: number, result: string = ''): Observable<UserFeedbackApiResponse> {
    const payload: GetUserFeedbackDataRequest = { fbType, result };
    return this.http.post<UserFeedbackApiResponse>(
      `${environment.apiUrl}Feedback/GetUserFeedbackData`,
      payload
    );
  }

  getAgentUserNames(): Observable<AgentUserNamesApiResponse> {
    return this.http.get<AgentUserNamesApiResponse>(`${environment.apiUrl}Feedback/GetAgentUserNames`);
  }

  saveFeedBackRequestAgent(payload: SaveFeedBackRequestAgentRequest): Observable<any> {
    return this.http.post(`${environment.apiUrl}Feedback/SaveFeedBackRequestAgent`, payload);
  }

  updateFeedbackAgentAssignment(payload: UpdateFeedbackAgentAssignmentRequest): Observable<any> {
    return this.http.post(`${environment.apiUrl}Feedback/UpdateFeedbackAgentAssignment`, payload);
  }

  getFeedBackRequests(agentId: number): Observable<FeedbackRequestItem[]> {
    const payload: FetchFeedBackRequestsPayload = { agent_id: agentId };
    return this.http.post<FeedbackRequestItem[]>(
      `${environment.apiUrl}Feedback/FethFeedBackRequests`,
      payload
    );
  }

  getPendingFeedbackRequests(agentId: number): Observable<PendingFeedbackRequestsResponse> {
    const params = new HttpParams().set('agentId', agentId.toString());
    return this.http.get<PendingFeedbackRequestsResponse>(
      `${environment.apiUrl}Feedback/GetPendingFeedbackRequests`,
      { params }
    );
  }

  getAssignedFeedbackRequestsPending(agentId: number): Observable<AssignedFeedbackRequestsResponse> {
    const params = new HttpParams().set('agentId', agentId.toString());
    return this.http.get<AssignedFeedbackRequestsResponse>(
      `${environment.apiUrl}Feedback/GetAssignedFeedbackRequestsPending`,
      { params }
    );
  }

  getLatestFeedBackRequestAgent(): Observable<LatestFeedbackRequestApiResponse> {
    return this.http.get<LatestFeedbackRequestApiResponse>(
      `${environment.apiUrl}Feedback/GetLatestFeedBackRequestAgent`
    );
  }

  feedAgentAccept(payload: FeedAgentAcceptPayload): Observable<FeedAgentAcceptResponse> {
    return this.http.post<FeedAgentAcceptResponse>(
      `${environment.apiUrl}Feedback/AcceptFeedbackAgent`,
      payload
    );
  }
  getFeedBackReviewData(payload: GetFeedBackReviewDataRequest): Observable<GetFeedBackReviewDataResponse> {
    return this.http.post<GetFeedBackReviewDataResponse>(
      `${environment.apiUrl}Feedback/GetFeedBackReviewData`,
      payload
    );
  }

  displayAgentReview(payload: DisplayAgentReviewRequest): Observable<DisplayAgentReviewResponse> {
    return this.http.post<DisplayAgentReviewResponse>(
      `${environment.apiUrl}Feedback/DisplayAgentReview`,
      payload
    );
  }

  getFeedbackChatHistory(payload: FeedbackChatHistoryRequest): Observable<FeedbackChatHistoryResponse> {
    const params = new HttpParams()
      .set('Session_Id', payload.Session_Id)
      .set('MobileNumber', this.withCountryCode(payload.MobileNumber));
    return this.http.get<FeedbackChatHistoryResponse>(
      `${environment.apiUrl}Chat/GetUserChatHistory`,
      { params }
    );
  }

  // Stored procedure tables key mobile numbers with the 91 country code prefix.
  private withCountryCode(mobileNumber: string): string {
    const digits = (mobileNumber || '').trim();
    return digits && !digits.startsWith('91') && !digits.startsWith('971') ? `91${digits}` : digits;
  }

  sendMessageToFbAgent(payload: MessageSendToFbAgentPayload): Observable<MessageSendToFbAgentResponse> {
    return this.http.post<MessageSendToFbAgentResponse>(
      `${environment.apiUrl}Feedback/MessageSendToFbAgent`,
      payload
    );
  }

  exitFeedbackAgent(payload: FeedbackExitAgentPayload): Observable<FeedbackExitAgentResponse> {
    return this.http.post<FeedbackExitAgentResponse>(
      `${environment.apiUrl}Feedback/ExitFeedbackAgent`,
      payload
    );
  }

  storeAgentFeedback(payload: StoreAgentFeedBackPayload): Observable<StoreAgentFeedBackResponse> {
    return this.http.post<StoreAgentFeedBackResponse>(
      `${environment.apiUrl}Feedback/StoreAgentFeedBack`,
      payload
    );
  }

  submitFeedbackCampaign(payload: FeedbackCampaignSubmitRequest): Observable<any> {
    return this.http.post(`${environment.apiUrl}Feedback/SubmitFeedbackCampaign`, payload);
  }
}
